import type { ReportWorkbook } from '../api'
import { reportToWorkbook } from './excel-workbook'
import type { ExcelReport } from './excel-workbook'

/**
 * What the preview dialog is showing, if anything.
 *
 * A report run from the catalogue opens the dialog before its figures arrive —
 * `loading` until the API answers, then `ready` — so the click visibly does
 * something on a period that takes a few seconds to read. A failure stays in
 * the dialog with a way to ask again, rather than closing it on the merchant.
 *
 * `id` names one run: a retry keeps it, so the dialog stays open through it;
 * a new run takes the next, so nothing of the last workbook carries over.
 */
export type ExcelPreview =
  | { id: number; status: 'loading'; title: string }
  | { id: number; status: 'ready'; title: string; workbook: ReportWorkbook }
  | { id: number; status: 'failed'; title: string; error: unknown; retry: () => void }

/**
 * Module state rather than context: a report is run from the catalogue's
 * dialog, which closes as the preview opens, and `ExcelPreviewHost` — mounted
 * once in the shell — is the only reader.
 */
let current: ExcelPreview | null = null
let nextId = 1
const listeners = new Set<() => void>()

function publish(next: ExcelPreview | null) {
  current = next
  listeners.forEach((listener) => listener())
}

/** Opens a finished workbook (a server export) in the preview. Saving waits for the merchant. */
export function openExcelPreview(workbook: ReportWorkbook): void {
  publish({ id: nextId++, status: 'ready', title: workbook.fileName, workbook })
}

/** A report sheet built in the browser, laid out as the server's, as a workbook not yet saved. */
export async function excelWorkbook(report: ExcelReport): Promise<ReportWorkbook> {
  return { blob: await reportToWorkbook(report), fileName: report.fileName }
}

/** Builds a report sheet in the browser, laid out as the server's, then opens it in the preview. */
export async function previewExcel(report: ExcelReport): Promise<void> {
  openExcelPreview(await excelWorkbook(report))
}

/**
 * Opens the preview at once, loading, and fills it with whatever `build`
 * resolves to — the report read from the API and laid out as a workbook.
 *
 * Closing the dialog before then drops the answer when it lands, so a slow
 * period can never reopen a dialog the merchant has already dismissed.
 */
export function runExcelPreview(title: string, build: () => Promise<ReportWorkbook>): void {
  run(nextId++, title, build)
}

function run(id: number, title: string, build: () => Promise<ReportWorkbook>) {
  publish({ id, status: 'loading', title })

  Promise.resolve()
    .then(build)
    .then(
      (workbook) => {
        if (current?.id === id) publish({ id, status: 'ready', title, workbook })
      },
      (error: unknown) => {
        if (current?.id === id) {
          publish({ id, status: 'failed', title, error, retry: () => run(id, title, build) })
        }
      },
    )
}

export function closeExcelPreview(): void {
  publish(null)
}

export function subscribeExcelPreview(listener: () => void): () => void {
  listeners.add(listener)

  return () => {
    listeners.delete(listener)
  }
}

export function currentExcelPreview(): ExcelPreview | null {
  return current
}
